import type { ReactNode } from 'react'
import styled from 'styled-components'

import { useRefWithCallback } from 'aem-libs-react-ts/src/hooks/useRefWithCallBack'
import { createTestId } from 'aem-libs-tests/src/utility/createTestId'
import type { Maybe } from 'aem-libs-ts/src/types/utility'

import tooltipIco from '../../../assets/tooltip.svg'
import tooltipWhiteIco from '../../../assets/tooltipWhite.svg'
import { BORDER_RADIUS } from '../../../styles'
import { tipLeftBasePosition, tipWidth } from './constants'
import { useTipPosition } from './useTipPosition'

const Container = styled('span').withConfig({
  shouldForwardProp: (prop) => !['invertedColor'].includes(prop)
})<{ invertedColor: boolean }>`
  position: relative;
  display: inline-block;
  width: 22px;
  height: 22px;
  background: url(${({ invertedColor }) => (invertedColor ? tooltipWhiteIco : tooltipIco)}) no-repeat center center;
  min-width: 22px;
  margin-left: 10px;
  background-size: 22px 22px;
  cursor: pointer;

  &:has(#tip-text[aria-hidden='true']) {
    &:before {
      visibility: hidden;
    }

    #tip-text {
      visibility: hidden;
    }
  }

  &:has(#tip-text[aria-hidden='false']) {
    &:before {
      visibility: visible;
    }

    #tip-text {
      visibility: visible;
    }
  }

  &:has(#tip-text[data-vertical-orientation='top']) {
    #tip-text {
      bottom: 30px;
    }
  }

  &:has(#tip-text[data-vertical-orientation='bottom']) {
    #tip-text {
      bottom: auto;
      top: 30px;
    }

    &:before {
      inset: 26px 0 auto 5px;
    }
  }

  &:before {
    content: '';
    visibility: hidden;
    background: ${({ theme }) => theme.colors.background.dark};
    width: 12px;
    height: 12px;
    position: absolute;
    transform: rotate(45deg);
    inset: auto 0 26px 5px;
    border-radius: 2px;
  }
`

const TipText = styled.span<{
  $positionLeft: Maybe<number>
}>`
  visibility: hidden;
  position: absolute;
  background: ${({ theme }) => theme.colors.background.dark};
  color: ${({ theme }) => theme.colors.font.white};
  font-size: 14px;
  padding: 10px;
  min-width: ${tipWidth}px;
  z-index: 1001;
  bottom: 30px;
  left: ${(props) => `${props.$positionLeft ?? tipLeftBasePosition}px`};
  border-radius: ${BORDER_RADIUS};
  text-align: left;
`

export const describedById = (fieldId: Maybe<string>) => (fieldId ? `tip-container-${fieldId}` : 'tip-container')

export interface TipProps {
  text: string | ReactNode
  invertedColor?: boolean
  fieldId?: string
  testId?: string
  className?: string
}

export const Tip = ({ text, invertedColor, fieldId, testId, className }: TipProps) => {
  const tipId = describedById(fieldId)
  const { tipMessageRef, leftPosition } = useTipPosition(tipId)

  const onMouseEnter = () => tipMessageRef.current?.setAttribute('aria-hidden', 'false')
  const onMouseLeave = () => {
    if (tipMessageRef.current?.hasAttribute('data-keep-visible')) {
      return
    }
    tipMessageRef.current?.setAttribute('aria-hidden', 'true')
  }

  const tipRef = useRefWithCallback<HTMLDivElement>(
    (node) => {
      node.addEventListener('mouseenter', onMouseEnter)
      node.addEventListener('mouseleave', onMouseLeave)
    },
    (node) => {
      node.removeEventListener('mouseenter', onMouseEnter)
      node.removeEventListener('mouseleave', onMouseLeave)
    }
  )

  return (
    <Container
      aria-label={'Tip: ' + text}
      id={tipId}
      invertedColor={invertedColor ?? false}
      role={'tooltip'}
      data-testid={createTestId(testId ?? 'tip', 'wrapper')}
      ref={tipRef}
      onClick={(e) => {
        e.preventDefault()

        if (tipMessageRef.current?.getAttribute('data-keep-visible')) {
          tipMessageRef.current.setAttribute('aria-hidden', 'true')
          tipMessageRef.current.removeAttribute('data-keep-visible')
          return
        }

        tipMessageRef.current?.setAttribute('aria-hidden', 'false')
        tipMessageRef.current?.setAttribute('data-keep-visible', 'true')
      }}
      tabIndex={0}
      {...(className ? { className } : {})}
    >
      <TipText
        id={'tip-text'}
        data-testid={testId}
        ref={tipMessageRef}
        $positionLeft={leftPosition}
        onClick={(e) => e.stopPropagation()}
      >
        {text}
      </TipText>
    </Container>
  )
}
